import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Flex,
  Text,
  Button,
  useToast,
  useDisclosure,
  Spinner,
  IconButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import { EditIcon, DeleteIcon } from '@chakra-ui/icons';
import SchoolModal from './SchoolModal';
import SearchControls from './components/SearchControls';
import PaginationControls from './components/PaginationControls';
import api from '../../../../utils/api';
import analytics from '../../../../segmentTracking';

const SchoolsTab = ({ refreshTrigger }) => {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentSchool, setCurrentSchool] = useState(null);
  const [deleteSchool, setDeleteSchool] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalSchools, setTotalSchools] = useState(0);
  const [currentUser] = useState(JSON.parse(localStorage.getItem('user')));
  const [currentInstitution] = useState(
    JSON.parse(localStorage.getItem('institution')),
  );

  const toast = useToast();
  const {
    isOpen: isModalOpen,
    onOpen: onModalOpen,
    onClose: onModalClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();

  const cancelRef = React.useRef();

  // Fetch schools
  const fetchSchools = useCallback(async () => {
    setLoading(true);
    try {
      const skip = (currentPage - 1) * itemsPerPage;
      const response = await api.get('/dashboard/schools', {
        params: {
          institution: currentInstitution?._id,
          skip: skip,
          limit: itemsPerPage,
          search: searchTerm,
        },
      });

      setSchools(response.data.schools || []);
      setTotalSchools(response.data.total || 0);

      // Track schools view
      analytics.track('settings:academic:schools:viewed', {
        schoolsCount: response.data.schools?.length || 0,
        totalSchools: response.data.total || 0,
        currentPage,
        itemsPerPage,
        searchTerm: searchTerm,
        userRole: currentUser?.role,
        email: currentUser?.email,
      });
    } catch (error) {
      console.error('Error fetching schools:', error);
      toast({
        title: 'Error',
        description:
          error?.response?.data?.message || 'Failed to fetch schools',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [
    searchTerm,
    currentInstitution,
    currentUser,
    toast,
    currentPage,
    itemsPerPage,
  ]);

  useEffect(() => {
    if (currentInstitution?._id) {
      fetchSchools();
    }
  }, [fetchSchools, currentInstitution]);

  // Refresh data when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger > 0 && currentInstitution?._id) {
      fetchSchools();
    }
  }, [refreshTrigger, fetchSchools, currentInstitution]);

  // Reset to page 1 when search term changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Handle add school
  const handleAddSchool = () => {
    setCurrentSchool(null);
    onModalOpen();

    analytics.track('settings:academic:schools:addclicked', {
      userRole: currentUser?.role,
      email: currentUser?.email,
    });
  };

  // Handle edit school
  const handleEditSchool = (school) => {
    setCurrentSchool(school);
    onModalOpen();

    analytics.track('settings:academic:schools:editclicked', {
      schoolId: school._id,
      schoolName: school.name,
      userRole: currentUser?.role,
      email: currentUser?.email,
    });
  };

  // Handle delete school
  const handleDeleteSchool = (school) => {
    setDeleteSchool(school);
    onDeleteOpen();

    analytics.track('settings:academic:schools:deleteclicked', {
      schoolId: school._id,
      schoolName: school.name,
      userRole: currentUser?.role,
      email: currentUser?.email,
    });
  };

  // Confirm delete school
  const confirmDeleteSchool = async () => {
    if (!deleteSchool) return;

    try {
      await api.delete(`/dashboard/schools/${deleteSchool._id}`);

      toast({
        title: 'Success',
        description: 'School deleted successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Track successful deletion
      analytics.track('settings:academic:schools:deleted', {
        schoolId: deleteSchool._id,
        schoolName: deleteSchool.name,
        userRole: currentUser?.role,
        email: currentUser?.email,
      });

      fetchSchools(); // Refresh list
    } catch (error) {
      console.error('Error deleting school:', error);
      toast({
        title: 'Error',
        description:
          error?.response?.data?.message || 'Failed to delete school',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      onDeleteClose();
      setDeleteSchool(null);
    }
  };

  // Handle successful save from modal
  const handleSchoolSaved = () => {
    fetchSchools(); // Refresh list
    onModalClose();
  };

  // Handle pagination changes
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
  };

  const handleSearchChange = (newSearchTerm) => {
    setSearchTerm(newSearchTerm);
  };

  return (
    <Box>
      {/* Search Controls */}
      <SearchControls
        searchPlaceholder="Search schools..."
        itemsPerPage={itemsPerPage}
        onItemsPerPageChange={handleItemsPerPageChange}
        onSearchChange={handleSearchChange}
        entityName="schools"
        analyticsPrefix="academic:schools"
        loading={loading}
      >
        <Button
          bg="#EF6506"
          color="white"
          _hover={{ bg: '#D55A05' }}
          onClick={handleAddSchool}
        >
          Add School
        </Button>
      </SearchControls>

      {/* Schools List */}
      <Box>
        {loading ? (
          <Flex justifyContent="center" alignItems="center" py={8}>
            <Spinner size="lg" color="#EF6506" />
          </Flex>
        ) : schools.length > 0 ? (
          <Flex flexDir="column" gap={2}>
            {schools.map((school) => (
              <Flex
                key={school._id}
                justifyContent="space-between"
                alignItems="center"
                p={4}
                bg="white"
                border="1px solid #E7E7E7"
                borderRadius="8px"
                _hover={{ bg: '#F9F9F9' }}
              >
                <Box>
                  <Text fontSize="16px" fontWeight="600" color="#222222" mb={1}>
                    {school.name}
                  </Text>
                  <Text fontSize="14px" color="#757575">
                    Created: {new Date(school.createdAt).toLocaleDateString()}
                  </Text>
                </Box>
                <Flex gap={2}>
                  <IconButton
                    aria-label="Edit school"
                    icon={<EditIcon />}
                    size="sm"
                    colorScheme="blue"
                    variant="outline"
                    onClick={() => handleEditSchool(school)}
                  />
                  <IconButton
                    aria-label="Delete school"
                    icon={<DeleteIcon />}
                    size="sm"
                    colorScheme="red"
                    variant="outline"
                    onClick={() => handleDeleteSchool(school)}
                  />
                </Flex>
              </Flex>
            ))}
          </Flex>
        ) : (
          <Box textAlign="center" py={8}>
            <Text fontSize="16px" color="#757575">
              {searchTerm
                ? 'No schools found matching your search.'
                : 'No schools found. Add your first school to get started.'}
            </Text>
          </Box>
        )}
      </Box>

      {/* Pagination Controls */}
      <PaginationControls
        currentPage={currentPage}
        totalItems={totalSchools}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        entityName="schools"
        analyticsPrefix="academic:schools"
        loading={loading}
      />

      {/* Add/Edit School Modal */}
      <SchoolModal
        isOpen={isModalOpen}
        onClose={onModalClose}
        school={currentSchool}
        onSchoolSaved={handleSchoolSaved}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete School
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete "{deleteSchool?.name}"? This
              action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={confirmDeleteSchool} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default SchoolsTab;
